import { Stack, styled } from '@mui/material';
import { GeneralFeedbackDialog, useOfferFeedbackDialog } from 'features/feedback';
import { LoadMoreButton, OfferListLoadingState } from 'features/myJobs';
import { getOfferKey } from 'pages/candidateOffer';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PageLayout } from 'shared/components';
import { useAppDispatch, useNavigateWithSearch } from 'shared/hooks';

import { DeclineOfferDialog, OfferListItem, SubmitDeclineCallback } from '../components';
import JobHistoryPreview from '../components/JobHistoryPreview';
import JobOffersOverview from '../components/JobOffersOverview';
import { useLoadMoreOffers, useLoadOffers, usePageStatus, useScrollToLastOpened } from '../hooks';
import {
    candidateOffersSelector,
    closeDeclineDialog,
    declineOfferStateSelector,
    declineOfferThunk,
    openDeclineDialog
} from '../store';
import { ListOffer } from '../types';

function OfferList() {
    const dispatch = useAppDispatch();
    const navigate = useNavigateWithSearch();
    const status = usePageStatus();

    useLoadOffers();
    const { loadMore, hasMorePages, isLoadingMore } = useLoadMoreOffers();

    const offers = useSelector(candidateOffersSelector);
    const declineOfferState = useSelector(declineOfferStateSelector);

    const previewRefMap = useScrollToLastOpened(offers);

    const { onOpen: openFeedbackDialog, ...feedbackDialogState } = useOfferFeedbackDialog('OfferFeedback');

    const handleOfferOpen = useCallback((orderId: string, orderRoleId: string) => {
        navigate(`./${ orderId }/${ orderRoleId }`);
    }, [ navigate ]);

    const handleOfferDecline = useCallback((offer: ListOffer) => {
        dispatch(openDeclineDialog({ offer }));
    }, [ dispatch ]);

    const handleDeclineDialogClose = useCallback(() => {
        dispatch(closeDeclineDialog());
    }, [ dispatch ]);

    const handleOfferFeedbackClick = useCallback((offer: ListOffer) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const orderId = 'orderId' in offer ? offer.orderId : (offer as any).id;

        openFeedbackDialog(orderId, offer.orderRoleId || '');
    }, [ openFeedbackDialog ]);

    const handleDeclineDialogSubmit = useCallback<SubmitDeclineCallback>(async (offer) => {
        return dispatch(declineOfferThunk({ offer }));
    }, [ dispatch ]);

    const createPreviewRefCallback = useCallback((offer: ListOffer) => {
        return (node: HTMLDivElement) => {
            previewRefMap.current[getOfferKey(offer.orderId, offer.orderRoleId)] = node;
        };
    }, [ previewRefMap ]);

    const isLoading = status === 'pending';

    return (
        <>
            <PageLayout variant="with-aside">
                <PageLayout.Content>
                    <ItemsContainer>
                        { isLoading && <OfferListLoadingState numberOfActions={ 2 } /> }

                        { offers.map((offer) => (
                            <OfferListItem key={ getOfferKey(offer.orderId, offer.orderRoleId) }
                                offer={ offer }
                                onOpen={ handleOfferOpen }
                                onReject={ handleOfferDecline }
                                onFeedbackClick={ handleOfferFeedbackClick }
                                ref={ createPreviewRefCallback(offer) }
                            />
                        )) }

                        { hasMorePages && (
                            <LoadMoreButton
                                onClick={ loadMore }
                                loading={ isLoadingMore }
                            />
                        ) }
                    </ItemsContainer>
                </PageLayout.Content>

                <PageLayout.Aside>
                    { <JobOffersOverview isLoading={ isLoading } /> }
                    { !isLoading && <JobHistoryPreview /> }
                    { /* Mocks for demonstration purposes */ }
                    { /* <ActivitiesMock />
                    <GetMoreMatchesMock />
                    <PlatformStatsMock /> */ }
                </PageLayout.Aside>
            </PageLayout>

            <DeclineOfferDialog dialogState={ declineOfferState }
                onCancel={ handleDeclineDialogClose }
                onConfirm={ handleDeclineDialogSubmit }
            />

            <GeneralFeedbackDialog { ...feedbackDialogState } />
        </>
    );
}

const ItemsContainer = styled(Stack)(({ theme }) => ({
    gap: theme.spacing(3)
}));

export default memo(OfferList);
